import React, { useState, useEffect } from "react"
import Img from "gatsby-image"
import { Button, Flex, Box, Container, Text } from "theme-ui"
import Header from "./header"
import { AnimatePresence, motion } from "framer-motion"
import Link from "./link"
import { getPagePath } from "../utils/pathUtils"
import { LangContext } from "../context/langContext"
import ArrowLink from "./arrowLink"
import { ChevronRight, ChevronLeft } from "react-feather"
import { CircularProgressbar, buildStyles } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import ChangingProgressProvider from "../templates/changingProgressProvider"

const AnimatedBox = motion(Box)
const AnimatedImage = motion(Box)
const AnimatedSlide = motion(Box)

const container = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 1.5,
    },
  },
}

const item = (members) => {
  return {
    hidden: { opacity: 0, scale: 1 },
    show: i => ({
      scale: 1.1,
      opacity: [0, 1, 1, 0],
      transition: {
        repeat: Infinity,
        repeatDelay: (i - 1),
        duration: 6 / i,
        time: [0, 0.001, 0.999, 1],
      },
    }),
  }
}

const HomeHero = ({ section, locale }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [currentStopIntervalStatus, setStopIntervalStatus] = useState(false);

  let interval = null;
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(
        currentSlide < section.slides.length - 1 ? currentSlide + 1 : 0
      )
    }, 4500)

    if (currentStopIntervalStatus) {
      clearInterval(interval);
    }

    return () => clearInterval(interval)

  }, [currentSlide])

  const handleSlideNext = () => {
    setStopIntervalStatus(true);
    setCurrentSlide(currentSlide < section.slides.length - 1 ? currentSlide + 1 : 0)
  }
  const handleSlidePrev = () => {
    setStopIntervalStatus(true);
    setCurrentSlide(currentSlide < 1 ? section.slides.length - 1 : currentSlide - 1)
  }

  return (
    <>
      <Box sx={{
        display: ["block", "block", "block"],
        position: ["absolute", "absolute"],
        right: [0, 0],
        top: ["500px", "50%"],
        transform: ["unset", "translateY(-50%)"],
        zIndex: 999
      }}>
        <Flex sx={{ alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
          <Button variant="arrowNext" onClick={handleSlideNext} >
            <Flex sx={{ alignItems: "center", justifyContent: "center"}}>
              <ChevronRight />
            </Flex>
          </Button>
          <Button variant='arrowPrev' onClick={handleSlidePrev}>
            <Flex sx={{ alignItems: "center", justifyContent: "center"}}>
              <ChevronLeft />
            </Flex>
          </Button>
        </Flex>
      </Box>
      <Box
        sx={{
          minHeight: ["auto", "1200px", "890px"],
          paddingBottom: ["1.5rem"],
          ".gatsby-image-wrapper": { height: "100%" },
          ".swiper-container": { height: ["980px"] },
          backgroundColor: "primary",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {section.slides.length > 0 && (
          <Box
            sx={{
              position: "relative",
              height: ["auto", "1200px", "890px"],
              width: "100%",
            }}
          >
            <Header lang={locale} />
            {section.slides.map((slide, index) => (
              <AnimatePresence>
                {currentSlide === index && (
                  <AnimatedSlide
                    key={slide.id}
                    variants={container}
                    initial="hidden"
                    animate="show"
                    exit={{
                      opacity: 0,
                      transition: {
                        duration: 0.2,
                      },
                    }}
                  >
                    <Container
                      variant="header"
                      sx={{ position: "relative", px: [4], zIndex: 99 }}
                    >
                      <BoxedText slide={slide} index={index} />
                    </Container>
                    <Box
                      sx={{
                        position: ["relative", "absolute"],
                        width: ["80%", "700px"],
                        height: ["80%", "700px"],
                        left: ["10%", "25%", "40%", "45%"],
                        bottom: ["unset", 50],
                        top: ["65%", "unset"],
                        zIndex: 1,
                      }}
                    >
                      <ChangingProgressProvider
                        values={
                          slide.images.length === 2
                            ? [0, 50, 100]
                            : [0, 33, 66, 100]
                        }
                        interval={slide.images.length === 2 ? 1300 : 1000}
                      >
                        {percentage => (
                          <CircularProgressbar
                            value={percentage}
                            styles={buildStyles({
                              pathColor: "#ffffff",
                              trailColor: "transparent",
                              strokeLinecap: "butt",
                              pathTransition:
                                percentage === 0
                                  ? "none"
                                  : "stroke-dashoffset 0.5s ease 0s",
                            })}
                            counterClockwise
                          />
                        )}
                      </ChangingProgressProvider>
                      <Box
                        sx={{
                          position: ["absolute"],
                          height: ["100%"],
                          width: "100%",
                          top: 0,
                          left: 0,
                        }}
                      >
                        {slide.images.map((image, i) => (
                          <AnimatedImage
                            sx={{
                              position: "absolute",
                              height: ["100%", "100%"],
                              width: ["100%", "100%"],
                              top: ["0%", 0],
                              zIndex: -1,
                              left: [0, 0]
                            }}
                            key={i}
                            custom={slide.images.length}
                            variants={item(slide.images.length)}

                          >
                            <Box sx={{
                              height: ["130%", "130%"],
                              width: ["180%", "180%"],
                              position: ["relative"],
                              left: ["-70%", "-70%"],
                              bottom: ["26%", "20%"]
                            }}>
                              <Img fluid={image.fluid} />
                            </Box>
                          </AnimatedImage>
                        ))}
                      </Box>
                    </Box>
                  </AnimatedSlide>
                )}
              </AnimatePresence>
            ))}
          </Box>
        )}
      </Box>
    </>
  )
}

const BoxedText = ({ slide, index }) => {
  return (
    <AnimatedBox
      initial={{ opacity: 0, y: 10, scale: 1 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      sx={{
        position: ["relative"],
        width: ["100%", "50%", "40%"],
        py: [4, 5, 6, "20vh"],
        
      }}
      key={index}
    >
      <Text variant="display" sx={{ mb: 4, fontSize: [5, 5, 7] }}>
        {slide.title}
      </Text>
      <Box dangerouslySetInnerHTML={{ __html: slide.body }} sx={{ mb: 4 }} />
      <Link
        to={getPagePath(slide.page)}
        sx={{ color: "light", textDecoration: "none" }}
      >
        <LangContext.Consumer>
          {t =>
            <>
              <ArrowLink dark anchor={t.discoverMore} />
            </>
          }
        </LangContext.Consumer>
      </Link>

    </AnimatedBox>
  )
}

export default HomeHero
